import React, { useState, useEffect, useRef } from 'react';
import { Zap, Check, Rocket, LayoutDashboard, Calendar, Workflow, MessageSquare } from 'lucide-react';
import { useTheme } from '@/contexts/ThemeContext';
import AnimatedBorderBadge from '@/components/editorial/AnimatedBorderBadge';

const EditorialMultiPlatformSection: React.FC = () => {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const [isVisible, setIsVisible] = useState(false);
  const [responseTime, setResponseTime] = useState(0);
  const [completedSteps, setCompletedSteps] = useState([false, false, false, false]);
  const [showStat, setShowStat] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  const features = [
    { text: "Speed to Lead, Automated Nurturing Engine", icon: Rocket },
    { text: "Visual Sales Pipeline", icon: LayoutDashboard },
    { text: "Booking & Scheduling", icon: Calendar },
    { text: "Workflow Automation", icon: Workflow },
    { text: "Centralized Communication", icon: MessageSquare }
  ];

  const journeySteps = [
    { step: 'Lead Captured', time: '0s', icon: '📩' },
    { step: 'AI Qualification', time: '0.5s', icon: '🤖' },
    { step: 'Personalized Response', time: '1.2s', icon: '💬' },
    { step: 'Meeting Booked', time: '4m', icon: '📅' }
  ];

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !isVisible) {
          setIsVisible(true);
        }
      },
      { threshold: 0.3 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, [isVisible]);

  useEffect(() => {
    if (!isVisible) return;

    // Animate response time
    const duration = 1500;
    const targetTime = 1.2;
    const steps = 30;
    let step = 0;
    
    const timer = setInterval(() => {
      step++;
      setResponseTime(Number((targetTime * (step / steps)).toFixed(1)));
      if (step >= steps) clearInterval(timer);
    }, duration / steps);

    // Animate journey steps
    setTimeout(() => setCompletedSteps([true, false, false, false]), 400);
    setTimeout(() => setCompletedSteps([true, true, false, false]), 800);
    setTimeout(() => setCompletedSteps([true, true, true, false]), 1200);
    setTimeout(() => setCompletedSteps([true, true, true, true]), 1600);

    // Show stat bar
    setTimeout(() => setShowStat(true), 2000);

    return () => clearInterval(timer);
  }, [isVisible]);

  return (
    <section ref={sectionRef} className="py-16 px-[7%] sm:px-6">
      <div className="max-w-6xl mx-auto">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left Visual - Lead Response System */}
          <div className="order-2 lg:order-1">
            <div 
              className="rounded-xl p-6 overflow-hidden"
              style={{ 
                backgroundColor: isDark ? '#1A1A1A' : '#F8F8F8', 
                border: isDark ? '1px solid #333333' : '1px solid #E5E5E5',
                opacity: isVisible ? 1 : 0,
                transform: isVisible ? 'translateY(-4px) scale(1)' : 'translateY(20px) scale(0.95)',
                boxShadow: isVisible ? (isDark ? '0 20px 40px -10px rgba(0, 0, 0, 0.5)' : '0 20px 40px -10px rgba(0, 0, 0, 0.15), 0 10px 20px -5px rgba(0, 0, 0, 0.1)') : 'none',
                transition: 'all 0.6s ease-out'
              }}
            >
              {/* Header */}
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Zap className={`w-4 h-4 ${isDark ? 'text-amber-400' : 'text-amber-500'}`} />
                  <span className="text-sm font-medium" style={{ color: isDark ? '#FFFFFF' : '#111111' }}>Speed to Lead</span>
                </div>
                <span 
                  className={`px-2 py-0.5 text-[10px] font-medium rounded-full ${isDark ? 'bg-emerald-900/50 text-emerald-400' : 'bg-emerald-100 text-emerald-600'}`}
                  style={{ animation: isVisible ? 'pulse 2s ease-in-out infinite' : 'none' }}
                >
                  Active
                </span>
              </div>

              {/* Response Time Stats */}
              <div 
                className="rounded-lg p-4 mb-4 relative overflow-hidden"
                style={{ backgroundColor: isDark ? '#222222' : '#FFFFFF', border: isDark ? '1px solid #333333' : '1px solid #E0E0E0' }}
              >
                {/* Animated glow */}
                <div 
                  className="absolute inset-0 pointer-events-none"
                  style={{
                    background: 'radial-gradient(circle at center, rgba(16, 185, 129, 0.1) 0%, transparent 70%)',
                    opacity: isVisible ? 1 : 0,
                    transition: 'opacity 1s ease-out'
                  }}
                />
                <div className="text-center mb-3 relative z-10">
                  <span 
                    className="text-3xl font-bold"
                    style={{
                      color: isDark ? '#FFFFFF' : '#111111',
                      textShadow: isVisible ? '0 0 20px rgba(16, 185, 129, 0.3)' : 'none',
                      transition: 'text-shadow 0.5s ease-out'
                    }}
                  >
                    {responseTime}
                  </span>
                  <span className="text-lg font-medium" style={{ color: isDark ? '#888888' : '#666666' }}>sec</span>
                  <p className="text-[10px] mt-1" style={{ color: isDark ? '#666666' : '#888888' }}>Average Response Time</p>
                </div>
                <div className="flex items-center justify-between text-[10px] relative z-10">
                  <span style={{ color: isDark ? '#888888' : '#666666' }}>Industry Avg: 47 hours</span>
                  <span 
                    className={`font-medium ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`}
                    style={{
                      animation: isVisible ? 'flashIn 0.5s ease-out 1.5s both' : 'none'
                    }}
                  >
                    141,000x faster
                  </span>
                </div>
              </div>

              {/* Lead Journey with staggered animation */}
              <div className="space-y-2">
                {journeySteps.map((item, index) => (
                  <div 
                    key={index}
                    className="rounded-lg p-2 flex items-center gap-3"
                    style={{ 
                      backgroundColor: completedSteps[index] && index === journeySteps.length - 1 
                        ? (isDark ? '#10B98120' : '#10B98110') 
                        : (isDark ? '#222222' : '#FFFFFF'), 
                      border: `1px solid ${completedSteps[index] && index === journeySteps.length - 1 
                        ? '#10B98140' 
                        : (isDark ? '#333333' : '#E0E0E0')}`,
                      opacity: completedSteps[index] ? 1 : 0.4,
                      transform: completedSteps[index] ? 'translateX(0)' : 'translateX(-10px)',
                      transition: 'all 0.4s ease-out'
                    }}
                  >
                    <span 
                      className="text-base"
                      style={{
                        animation: completedSteps[index] ? `bounceIn 0.4s ease-out` : 'none'
                      }}
                    >
                      {item.icon}
                    </span>
                    <div className="flex-1">
                      <span className="text-xs font-medium" style={{ color: isDark ? '#FFFFFF' : '#111111' }}>{item.step}</span>
                    </div>
                    <span 
                      className="text-[10px] font-medium" 
                      style={{ 
                        color: completedSteps[index] && index === journeySteps.length - 1 ? '#10B981' : (isDark ? '#666666' : '#888888')
                      }}
                    >
                      {item.time}
                    </span>
                    {completedSteps[index] && index < journeySteps.length - 1 && (
                      <div 
                        className={`w-4 h-4 rounded-full flex items-center justify-center ${isDark ? 'bg-emerald-900/50' : 'bg-emerald-100'}`}
                        style={{
                          animation: 'scaleIn 0.3s ease-out'
                        }}
                      >
                        <Check className={`w-2.5 h-2.5 ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`} />
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {/* Stat Bar */}
              <div 
                className="mt-4 p-3 rounded-lg" 
                style={{ 
                  backgroundColor: isDark ? '#422006' : '#FEF3C7', 
                  border: isDark ? '1px solid #854d0e' : '1px solid #FCD34D',
                  opacity: showStat ? 1 : 0,
                  transform: showStat ? 'translateY(0)' : 'translateY(10px)',
                  transition: 'all 0.5s cubic-bezier(0.34, 1.56, 0.64, 1)'
                }}
              >
                <div className="flex items-center gap-2">
                  <span className="text-lg">🏆</span>
                  <span className={`text-xs font-medium ${isDark ? 'text-amber-300' : 'text-amber-800'}`}>
                    78% of customers buy from the first to reply
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Right Content */}
          <div className="order-1 lg:order-2">
            {/* Badge with animated border */}
            <AnimatedBorderBadge text="Automate" icon={Zap} align="left" />

            <h2 
              className="text-3xl lg:text-4xl font-bold tracking-tight mb-4"
              style={{ color: isDark ? '#FFFFFF' : '#111111' }}
            >
              Never Lose a Lead Again.
            </h2>

            <p 
              className="text-sm mb-8"
              style={{ color: isDark ? '#AAAAAA' : '#555555' }}
            >
              <span className="font-bold">78% of customers buy from the first to reply.</span> Speed isn't just a metric; it's your biggest competitive advantage.
            </p>

            {/* Feature checklist */}
            <div className="space-y-3 mb-8">
              {features.map((feature, index) => (
                <div 
                  key={index} 
                  className="flex items-start gap-3"
                  style={{
                    opacity: isVisible ? 1 : 0,
                    transform: isVisible ? 'translateX(0)' : 'translateX(-20px)',
                    transition: `all 0.5s ease-out ${index * 100}ms`
                  }}
                >
                  <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${isDark ? 'bg-emerald-900/50' : 'bg-emerald-100'}`}>
                    <Check className={`w-3 h-3 ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`} />
                  </div>
                  <span className="text-sm" style={{ color: isDark ? '#CCCCCC' : '#333333' }}>{feature.text}</span>
                </div>
              ))}
            </div>

            {/* CTA Button */}
            <button 
              onClick={() => window.location.href = '/pricing#pricing-section'}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200 hover:scale-[1.02] hover:shadow-md active:scale-[0.98] hover:opacity-90"
              style={{
                backgroundColor: isDark ? '#FFFFFF' : '#000000',
                color: isDark ? '#000000' : '#FFFFFF',
              }}
            >
              Activate Speed to Lead →
            </button>
          </div>
        </div>
      </div>

    </section>
  );
};

export default EditorialMultiPlatformSection;
